import React, { useState, useEffect } from "react";
import {
  Users,
  Search,
  Edit,
  Trash2,
  Shield,
  ShieldCheck,
  Calendar,
  Eye,
  Ban,
  UserCheck,
  User,
  UserCog,
  Loader,
  Key,
  Mail,
  Clock,
  Award,
  BookOpen,
  TrendingUp,
} from "lucide-react";
import apiService from "../../services/api";
import "./UserManagement.css";

const UserManagement = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("All");
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users`,
      );
      const data = await response.json();

      if (data.success) {
        setUsers(data.users);
        setError(null);
      } else {
        setError("Failed to fetch users");
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (userData) => {
    try {
      setActionLoading("edit");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userData.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            full_name: userData.name,
            email: userData.email,
            role: userData.role,
          }),
        },
      );

      const data = await response.json();

      if (data.success) {
        await fetchUsers(); // Refresh the list
        setShowEditModal(false);
        setEditingUser(null);
      } else {
        alert(data.error || "Failed to update user");
      }
    } catch (err) {
      console.error("Error updating user:", err);
      alert("Failed to update user");
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      setActionLoading(`status-${userId}`);
      const newStatus = currentStatus === "active" ? false : true;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userId}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ is_active: newStatus }),
        },
      );

      const data = await response.json();

      if (data.success) {
        await fetchUsers(); // Refresh the list
      } else {
        alert(data.error || "Failed to update user status");
      }
    } catch (err) {
      console.error("Error updating user status:", err);
      alert("Failed to update user status");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete user "${userName}"? This action cannot be undone.`,
      )
    ) {
      return;
    }

    try {
      setActionLoading(`delete-${userId}`);
      const data = await apiService.admin.deleteUser(userId);

      if (data && data.success) {
        await fetchUsers(); // Refresh the list
        alert("User deleted successfully");
      } else {
        alert(data?.error || "Failed to delete user");
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      alert(`Failed to delete user: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (passwordData) => {
    try {
      setActionLoading("password");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${passwordData.userId}/password`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ new_password: passwordData.password }),
        },
      );

      const data = await response.json();

      if (data.success) {
        setShowPasswordModal(false);
        setPasswordUser(null);
        alert("Password updated successfully");
      } else {
        alert(data.error || "Failed to update password");
      }
    } catch (err) {
      console.error("Error updating password:", err);
      alert("Failed to update password");
    } finally {
      setActionLoading(null);
    }
  };

  const handleViewUser = async (user) => {
    try {
      setActionLoading(`view-${user.id}`);
      setSelectedUser(user);
      setShowDetailsModal(true);

      const response = await fetch(
        `http://localhost:3001/api/admin/users/${user.id}`,
      );
      const data = await response.json();

      if (data.success) {
        setUserDetails(data.user);
      } else {
        alert(data.error || "Failed to fetch user details");
        setShowDetailsModal(false);
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      alert("Failed to fetch user details");
      setShowDetailsModal(false);
    } finally {
      setActionLoading(null);
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = selectedRole === "All" || user.role === selectedRole;
    const matchesStatus =
      selectedStatus === "All" || user.status === selectedStatus;

    return matchesSearch && matchesRole && matchesStatus;
  });

  const getRoleIcon = (role) => {
    return role === "admin" ? <ShieldCheck size={16} /> : <Shield size={16} />;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: "#10b981", bg: "#d1fae5", text: "Active" },
      inactive: { color: "#f59e0b", bg: "#fef3c7", text: "Inactive" },
      suspended: { color: "#ef4444", bg: "#fee2e2", text: "Suspended" },
    };

    const config = statusConfig[status] || statusConfig.inactive;

    return (
      <span
        className="status-badge"
        style={{
          color: config.color,
          backgroundColor: config.bg,
        }}
      >
        {config.text}
      </span>
    );
  };

  return (
    <div className="user-management">
      <div className="user-management-header">
        <div className="header-content">
          <h1>
            <Users size={24} />
            User Management
          </h1>
          <p>Manage users, roles, and permissions</p>
        </div>
        {/* <button
          className="btn btn-primary"
          onClick={() => console.log("Add user clicked")}
        >
          <Plus size={18} />
          Add User
        </button> */}
      </div>

      {/* Loading State */}
      {loading && (
        <div
          className="loading-container"
          style={{ textAlign: "center", padding: "2rem" }}
        >
          <Loader size={24} className="spinner" />
          <p>Loading users...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div
          className="error-container"
          style={{
            background: "#fee2e2",
            color: "#dc2626",
            padding: "1rem",
            borderRadius: "8px",
            margin: "1rem 0",
          }}
        >
          <p>Error: {error}</p>
          <button
            onClick={fetchUsers}
            className="btn btn-secondary"
            style={{ marginTop: "0.5rem" }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Main Content - Only show when not loading and no error */}
      {!loading && !error && (
        <>
          {/* Filters */}
          <div className="user-filters">
            <div className="search-box">
              <Search size={18} />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="filter-group">
              <div className="filter-item">
                <label>Role</label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  <option value="All">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                </select>
              </div>

              <div className="filter-item">
                <label>Status</label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  <option value="All">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="users-table-container">
            <div className="users-table">
              <div className="table-header">
                <div className="table-cell">User</div>
                <div className="table-cell">Role</div>
                <div className="table-cell">Status</div>
                <div className="table-cell">Join Date</div>
                <div className="table-cell">Last Active</div>
                <div className="table-cell">Assessments</div>
                {/* <div className="table-cell">Avg Score</div> */}
                <div className="table-cell">Actions</div>
              </div>

              {filteredUsers.map((user) => (
                <div key={user.id} className="table-row">
                  <div className="table-cell">
                    <div className="user-info">
                      <div className="user-avatar">
                        {user.role === "admin" ? (
                          <UserCog size={20} />
                        ) : (
                          <User size={20} />
                        )}
                      </div>
                      <div className="user-details">
                        <div className="user-name">{user.name}</div>
                        <div className="user-email">{user.email}</div>
                      </div>
                    </div>
                  </div>

                  <div className="table-cell">
                    <div className="role-badge">
                      {getRoleIcon(user.role)}
                      <span className={`role-text ${user.role}`}>
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    </div>
                  </div>

                  <div className="table-cell">
                    {getStatusBadge(user.status)}
                  </div>

                  <div className="table-cell">
                    <div className="date-info">
                      <Calendar size={14} />
                      {new Date(
                        user.joindate || user.joinDate,
                      ).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="table-cell">
                    <div className="date-info">
                      {new Date(
                        user.lastactive || user.lastActive,
                      ).toLocaleDateString()}
                    </div>
                  </div>

                  <div className="table-cell">
                    <span className="assessment-count">{user.assessments}</span>
                  </div>

                  {/* <div className="table-cell">
                    <span className="avg-score">
                      {user.avgscore > 0 ? `${user.avgscore}%` : "N/A"}
                    </span>
                  </div> */}

                  <div className="table-cell">
                    <div className="action-buttons">
                      <button
                        className="action-btn view"
                        title="View Details"
                        onClick={() => handleViewUser(user)}
                        disabled={actionLoading === `view-${user.id}`}
                      >
                        {actionLoading === `view-${user.id}` ? (
                          <Loader size={14} className="spinner" />
                        ) : (
                          <Eye size={14} />
                        )}
                      </button>
                      <button
                        className="action-btn edit"
                        title="Edit User"
                        onClick={() => {
                          setEditingUser(user);
                          setShowEditModal(true);
                        }}
                        disabled={actionLoading === "edit"}
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        className="action-btn password"
                        title="Reset Password"
                        onClick={() => {
                          setPasswordUser(user);
                          setShowPasswordModal(true);
                        }}
                        disabled={actionLoading === "password"}
                      >
                        <Key size={14} />
                      </button>
                      <button
                        className={`action-btn ${
                          user.status === "active" ? "suspend" : "enable"
                        }`}
                        title={
                          user.status === "active"
                            ? "Disable User"
                            : "Enable User"
                        }
                        onClick={() => handleToggleStatus(user.id, user.status)}
                        disabled={actionLoading === `status-${user.id}`}
                      >
                        {actionLoading === `status-${user.id}` ? (
                          <Loader size={14} className="spinner" />
                        ) : user.status === "active" ? (
                          <Ban size={14} />
                        ) : (
                          <UserCheck size={14} />
                        )}
                      </button>
                      <button
                        className="action-btn delete"
                        title="Delete User"
                        onClick={() => handleDeleteUser(user.id, user.name)}
                        disabled={actionLoading === `delete-${user.id}`}
                      >
                        {actionLoading === `delete-${user.id}` ? (
                          <Loader size={14} className="spinner" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Edit User Modal */}
          {showEditModal && editingUser && (
            <EditUserModal
              user={editingUser}
              onSave={handleEditUser}
              onCancel={() => {
                setShowEditModal(false);
                setEditingUser(null);
              }}
              loading={actionLoading === "edit"}
            />
          )}

          {/* Password Reset Modal */}
          {showPasswordModal && passwordUser && (
            <PasswordResetModal
              user={passwordUser}
              onSave={handleResetPassword}
              onCancel={() => {
                setShowPasswordModal(false);
                setPasswordUser(null);
              }}
              loading={actionLoading === "password"}
            />
          )}

          {/* User Details Modal */}
          {showDetailsModal && selectedUser && (
            <UserDetailsModal
              user={selectedUser}
              userDetails={userDetails}
              onClose={() => {
                setShowDetailsModal(false);
                setSelectedUser(null);
                setUserDetails(null);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};

// Edit User Modal Component
const EditUserModal = ({ user, onSave, onCancel, loading }) => {
  const [formData, setFormData] = useState({
    name: user.name || "",
    email: user.email || "",
    role: user.role || "user",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({ ...user, ...formData });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Edit User</h3>
          <button className="modal-close" onClick={onCancel}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
            />
          </div>

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              required
            />
          </div>

          <div className="form-group">
            <label>Role</label>
            <select
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? (
                <Loader size={16} className="spinner" />
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Password Reset Modal Component
const PasswordResetModal = ({ user, onSave, onCancel, loading }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validation
    if (password.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setError("");
    onSave({ userId: user.id, password });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Reset Password for {user.name}</h3>
          <button className="modal-close" onClick={onCancel}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {error && (
            <div
              className="error-message"
              style={{
                background: "#fee2e2",
                color: "#dc2626",
                padding: "0.75rem",
                borderRadius: "0.5rem",
                marginBottom: "1rem",
                fontSize: "0.875rem",
              }}
            >
              {error}
            </div>
          )}

          <div className="form-group">
            <label>New Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password (min 6 characters)"
              required
              minLength={6}
            />
          </div>

          <div className="form-group">
            <label>Confirm Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              required
              minLength={6}
            />
          </div>

          <div className="modal-actions">
            <button
              type="button"
              onClick={onCancel}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? (
                <Loader size={16} className="spinner" />
              ) : (
                "Reset Password"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// User Details Modal Component
const UserDetailsModal = ({ user, userDetails, onClose }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "N/A";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content user-details-modal">
        <div className="modal-header">
          <h3>User Details</h3>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {!userDetails ? (
            <div
              className="loading-container"
              style={{ textAlign: "center", padding: "2rem" }}
            >
              <Loader size={24} className="spinner" />
              <p>Loading user details...</p>
            </div>
          ) : (
            <div className="user-details-content">
              {/* Basic Info */}
              <div className="details-section">
                <h4>Basic Information</h4>
                <div className="details-grid">
                  <div className="detail-item">
                    <User size={16} />
                    <div>
                      <label>Full Name</label>
                      <span>{userDetails.name}</span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <Mail size={16} />
                    <div>
                      <label>Email</label>
                      <span>{userDetails.email}</span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <Shield size={16} />
                    <div>
                      <label>Role</label>
                      <span className={`role-badge ${userDetails.role}`}>
                        {userDetails.role.charAt(0).toUpperCase() +
                          userDetails.role.slice(1)}
                      </span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <div className="status-indicator">
                      {userDetails.status === "active" ? (
                        <UserCheck size={16} />
                      ) : userDetails.status === "suspended" ? (
                        <Ban size={16} />
                      ) : (
                        <Clock size={16} />
                      )}
                    </div>
                    <div>
                      <label>Status</label>
                      <span
                        className={`status-badge status-${userDetails.status}`}
                      >
                        {userDetails.status.charAt(0).toUpperCase() +
                          userDetails.status.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Account Info */}
              <div className="details-section">
                <h4>Account Information</h4>
                <div className="details-grid">
                  <div className="detail-item">
                    <Calendar size={16} />
                    <div>
                      <label>Joined</label>
                      <span>
                        {formatDate(
                          userDetails.joindate || userDetails.joinDate,
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <Clock size={16} />
                    <div>
                      <label>Last Login</label>
                      <span>
                        {formatDate(
                          userDetails.lastlogin || userDetails.lastLogin,
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="detail-item">
                    <Calendar size={16} />
                    <div>
                      <label>Last Updated</label>
                      <span>
                        {formatDate(
                          userDetails.lastupdated || userDetails.lastUpdated,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Stats */}
              <div className="details-section">
                <h4>Performance Statistics</h4>
                <div className="stats-grid">
                  <div className="stat-card">
                    <BookOpen size={20} />
                    <div>
                      <span className="stat-number">
                        {userDetails.completedassessments ||
                          userDetails.completedAssessments ||
                          0}
                      </span>
                      <span className="stat-label">Completed Assessments</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <Award size={20} />
                    <div>
                      <span className="stat-number">
                        {userDetails.totalattempts ||
                          userDetails.totalAttempts ||
                          0}
                      </span>
                      <span className="stat-label">Total Attempts</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <TrendingUp size={20} />
                    <div>
                      <span className="stat-number">
                        {userDetails.avgscore || userDetails.avgScore || 0}%
                      </span>
                      <span className="stat-label">Average Score</span>
                    </div>
                  </div>
                  <div className="stat-card">
                    <Award size={20} />
                    <div>
                      <span className="stat-number">
                        {userDetails.bestscore || userDetails.bestScore || 0}%
                      </span>
                      <span className="stat-label">Best Score</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              {userDetails.recentAttempts &&
                userDetails.recentAttempts.length > 0 && (
                  <div className="details-section">
                    <h4>Recent Assessment Attempts</h4>
                    <div className="recent-attempts">
                      {userDetails.recentAttempts.map((attempt, index) => (
                        <div key={index} className="attempt-item">
                          <div className="attempt-info">
                            <span className="attempt-title">
                              {attempt.assessmenttitle ||
                                attempt.assessmentTitle}
                            </span>
                            <span className="attempt-date">
                              {formatDate(attempt.started_at)}
                            </span>
                          </div>
                          <div className="attempt-stats">
                            <span
                              className={`attempt-status status-${attempt.status}`}
                            >
                              {attempt.status}
                            </span>
                            {attempt.score && (
                              <span className="attempt-score">
                                {attempt.score}%
                              </span>
                            )}
                            {attempt.duration_seconds && (
                              <span className="attempt-duration">
                                {formatDuration(attempt.duration_seconds)}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
